import { customElement, property, state } from 'lit/decorators.js';
import { CSSResult, html, nothing, PropertyValues, unsafeCSS } from 'lit';
import { consume } from '@lit/context';
import styles from './purchased-action-menu.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { ActionItem, ActionMenu } from '../action-menu/action-menu.js';
import { productsContext } from '../../../scripts/context/wishlistContext.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import { ProductsState } from '../../../scripts/context/wishlist-state.js';
import { productMenuContext } from '../../../scripts/context/menuContext.js';
import { ProductMenuState } from '../../../scripts/context/menu-state.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';
import type { ActionMenuProductInfo } from '../action-menu-product-info/action-menu-product-info.js';
import '../action-menu-product-info/action-menu-product-info.js';

@customElement('ents-purchased-action-menu')
export class PurchasedActionMenu extends ActionMenu {
  protected testId: string = 'purchased-action-menu-layer';

  @consume({ context: productMenuContext, subscribe: true })
  @property({ attribute: false })
  private productMenuState!: ProductMenuState;

  @consume({ context: productsContext, subscribe: true })
  @property({ attribute: false })
  private productsState!: ProductsState;

  @state()
  private shownImage: HTMLImageElement | null = null;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(baseLayerStyles)];
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    const previousProductMenuState: ProductMenuState | undefined = _changedProperties.get('productMenuState');
    if (
      previousProductMenuState != null &&
      previousProductMenuState.context.activeMenu !== 'purchased' &&
      this.productMenuState.context.activeMenu === 'purchased' &&
      this.productMenuState.context.product
    ) {
      this.showLayer = true;
      this.waitForImageElement();
    }
  }

  private async waitForImageElement() {
    await this.updateComplete;

    const productInfo = this.shadowRoot?.querySelector('ents-action-menu-product-info') as ActionMenuProductInfo | null;

    if (productInfo) {
      await productInfo.updateComplete;
      this.shownImage = productInfo.getShownImage();
    }
  }

  protected updateStateOnClose() {
    if (this.productMenuState.context.activeMenu === 'purchased') {
      this.productMenuState.closeMenu();
    }
  }

  protected renderInformation() {
    return html` <ents-action-menu-product-info></ents-action-menu-product-info>`;
  }

  protected getApplicableActionItems() {
    return [...this.getViewLastOrderItem(), ...this.getRemoveItem()];
  }

  private getViewLastOrderItem(): ActionItem[] {
    const purchaseDetails = this.productMenuState.context.product?.purchaseDetails;
    if (!purchaseDetails?.orderUrl) {
      return [];
    }

    const sizes = this.getSizesPurchased(purchaseDetails.sizesPurchased);

    return [
      {
        label: html`
          <a
            href="${purchaseDetails.orderUrl}"
            class="ents-action-menu__order-details bdk-link-secondary"
            size="medium"
            data-test-ents-action-menu-order-details
          >
            <div data-test-ents-action-menu-order-title>${this.messages['ents_wishlist_check_order']}</div>
            ${
              sizes.length > 0
                ? html`
                    <div class="ents-copy-extra-small" data-test-ents-action-menu-order-sizes>
                      ${this.messages['ents_wishlist_product_size'] ?? 'Größe:'}
                      <span class="ents-copy-extra-small-bold ents-action-menu__order-details__value"
                        >${sizes.join(', ')}</span
                      >
                    </div>
                  `
                : nothing
            }
            <div class="ents-copy-extra-small" data-test-ents-action-menu-order-date>
              ${this.messages['ents_wishlist_order_date']}
              <span class="ents-copy-extra-small-bold ents-action-menu__order-details__value"
                >${purchaseDetails.purchasedFormatted}</span
              >
            </div>
            ${
              purchaseDetails.returnedFormatted
                ? html`
                    <div class="ents-copy-extra-small" data-test-ents-action-menu-return-date>
                      ${this.messages['ents_wishlist_return_date']}
                      <span class="ents-copy-extra-small-bold ents-action-menu__order-details__value"
                        >${purchaseDetails.returnedFormatted}</span
                      >
                    </div>
                  `
                : nothing
            }
          </a>
        `,
        arrow: true,
        trackingKey: 'view_order',
        callback: () => {
          globalThis.location.href = purchaseDetails.orderUrl!;
        },
      },
    ];
  }

  private getSizesPurchased(sizesPurchased?: string[]): string[] {
    return (sizesPurchased || []).filter((size) => size && size.trim() !== '');
  }

  private getRemoveItem(): ActionItem[] {
    if (this.productsState.isShared()) {
      return [];
    }
    const product = this.productMenuState.context.product;
    return [
      {
        label: this.messages['ents_wishlist_actionmenu_removeItem_long']!,
        arrow: true,
        trackingKey: 'remove',
        callback: () => {
          const selectedArticle = this.productMenuState.context.selectedArticle;
          this.executeWishlistRemoveWithUndo(
            product!,
            selectedArticle,
            this.isOnAnyCollection(),
            this.shownImage?.currentSrc ?? '',
          );
        },
      },
    ];
  }

  private isOnAnyCollection(): boolean {
    return this.productsState.isProductInAnyCollection(
      ProductIdentifier.createByProduct(this.productMenuState.context.product),
    );
  }
}
